import React, { useRef, useEffect, useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import Plyr from 'plyr';
// import Hls from 'hls.js';
import 'plyr/dist/plyr.css';

const asyncHls = import('hls.js');

// 导入本地Plyr资源文件
import plyrIconUrl from '../../assets/plyr/plyr.svg';
import plyrBlankVideo from '../../assets/plyr/blank.mp4';

import "./index.less"
import type Hls from 'hls.js';
import { isIOS } from 'src/utils'

interface VideoPlayerProps {
  videoUrl: string;
  subtitleUrl?: string;
  languageCode?: string;
  onPlay?: () => void;
  className?: string;
}

export interface VideoPlayerRef {
  setSubtitle: (subtitleUrl: string, languageCode?: string) => void;
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(({
  videoUrl, 
  subtitleUrl = '', 
  languageCode, 
  onPlay,
  className = ''
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<any>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const hlsInstanceRef = useRef<Hls | null>(null);
  const [currentSubtitleUrl, setCurrentSubtitleUrl] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [qualityLevels, setQualityLevels] = useState<any[]>([]);
  const [currentQuality, setCurrentQuality] = useState<number>(0);
  
  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    setSubtitle: (newSubtitleUrl: string) => {
      console.log('通过ref更新字幕:', newSubtitleUrl);
      if (newSubtitleUrl !== currentSubtitleUrl) {
        updateSubtitleTrack(newSubtitleUrl);
        setCurrentSubtitleUrl(newSubtitleUrl);
      }
    }
  }));

  // 检测是否为HLS/m3u8格式
  const isHlsUrl = useCallback((url: string) => {
    return url.includes('.m3u8') || url.includes('m3u8');
  }, []);

  // 处理分辨率切换
  const handleQualityChange = useCallback((qualityIndex: number) => {
    if (!hlsInstanceRef.current) return;

    console.log('切换到分辨率级别:', qualityIndex);
    setCurrentQuality(qualityIndex);

    // 直接设置到指定的分辨率级别
    hlsInstanceRef.current.currentLevel = qualityIndex;
    console.log('手动选择分辨率级别:', qualityIndex);
  }, []);



  // 清理当前播放器实例
  const destroyCurrentPlayer = useCallback(() => {
    // 清理HLS实例
    if (hlsInstanceRef.current) {
      try {
        hlsInstanceRef.current.destroy();
      } catch (error) {
        console.log('清理HLS实例时出错:', error);
      }
      hlsInstanceRef.current = null;
    }

    if (playerInstanceRef.current) {
      try {
        if (playerInstanceRef.current.destroy) {
          playerInstanceRef.current.destroy();
        }
      } catch (error) {
        console.log('清理播放器时出错:', error);
      }
      playerInstanceRef.current = null;
    }

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    
    // 重置状态
    setIsInitialized(false);
    setQualityLevels([]);
    setCurrentQuality(0);
  }, []);

  // 更新字幕轨道
  const updateSubtitleTrack = useCallback((newSubtitleUrl: string) => {
    if (!videoElementRef.current || !newSubtitleUrl) return;

    console.log('更新字幕轨道:', newSubtitleUrl);

    const currentPlayer = playerInstanceRef.current;
    if (!currentPlayer) {
      console.log('播放器实例不存在，无法更新字幕');
      return;
    }

    // 保存当前播放状态
    const videoElement = videoElementRef.current;
    const wasPlaying = !videoElement.paused;
    const currentTime = videoElement.currentTime;
    
    // 移除现有的字幕轨道
    const existingTracks = videoElement.querySelectorAll('track');
    existingTracks.forEach(track => {
      console.log('移除现有字幕轨道:', track.src);
      track.remove();
    });

    // 创建新的字幕轨道
    const srclang = languageCode ? languageCode.split('-')[0] : 'zh';
    
    const track = document.createElement('track');
    track.kind = 'subtitles';
    track.label = newSubtitleUrl.includes('blob:') ? '动态字幕' : 'English';
    track.srclang = srclang;
    track.src = newSubtitleUrl;
    
    // 重要：先添加到DOM但不立即启用
    videoElement.appendChild(track);

    // 使用异步方式处理字幕加载，避免阻塞视频播放
    const handleTrackLoad = () => {
      console.log('字幕轨道加载完成');
      
      // 启用字幕
      track.track.mode = 'showing';
      
      // 恢复播放状态
      if (wasPlaying) {
        setTimeout(() => {
          videoElement.play().catch(e => console.log('恢复播放失败:', e));
        }, 100);
      }
      
      // 移除事件监听器
      track.removeEventListener('load', handleTrackLoad);
    };

    track.addEventListener('load', handleTrackLoad);
    
    // 设置超时，如果字幕加载失败也继续
    setTimeout(() => {
      if (track.track) {
        track.track.mode = 'showing';
      }
    }, 2000);

    // 重要：不要调用 videoElement.load()，这会导致视频重新加载
    // 只需要设置track为default并启用即可
    track.default = true;

    // 如果使用Plyr，还需要更新Plyr的字幕状态
    if (currentPlayer && currentPlayer.captions) {
      try {
        setTimeout(() => {
          if (currentPlayer.captions) {
            currentPlayer.captions.toggle(true);
            currentPlayer.captions.active = true;
            console.log('Plyr字幕已启用');
          }
        }, 500);
      } catch (error) {
        console.log('更新Plyr字幕状态失败:', error);
      }
    }
  }, [languageCode]);

  // 预加载HLS信息
  const preloadHlsInfo = useCallback(async (videoUrl: string): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      asyncHls.then(({ default: Hls }) => {
        if (!Hls.isSupported()) {
          reject(new Error('HLS not supported'));
          return;
        }

        console.log('预加载HLS信息:', videoUrl);
        const tempVideo = document.createElement('video');
        const hls = new Hls({
          enableWorker: false,
          lowLatencyMode: true,
          backBufferLength: 90
        });

        let resolved = false;

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!resolved) {
            resolved = true;
            const levels = hls.levels;
            console.log('预加载获取到的分辨率级别:', levels);
            hls.destroy();
            resolve(levels || []);
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (!resolved) {
            resolved = true;
            console.error('预加载HLS出错:', data);
            hls.destroy();
            reject(new Error('Failed to load HLS manifest'));
          }
        });

        // 设置超时
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            hls.destroy();
            reject(new Error('HLS manifest load timeout'));
          }
        }, 10000);

        hls.loadSource(videoUrl);
        hls.attachMedia(tempVideo);
        
      });
    });
  }, []);

  // 创建Plyr播放器的配置
  const createPlyrConfig = useCallback((levels: any[] = []) => {
    // 构建质量选项
    let qualityOptions: number[] = [];
    let qualityMapping: any[] = [];
    let defaultQuality = 480; // 默认480p

    if (levels.length > 0) {
      // 按分辨率从高到低排序
      const sortedLevels = [...levels].sort((a, b) => b.height - a.height);
      qualityOptions = sortedLevels.map(level => level.height);
      
      qualityMapping = sortedLevels.map((level) => {
        const originalIndex = levels.findIndex(l => 
          l.height === level.height && l.width === level.width
        );
        return {
          plyrValue: level.height,
          hlsIndex: originalIndex,
          label: `${level.height}p`
        };
      });
      defaultQuality = qualityOptions[0];
    } else {
      // 如果没有分辨率信息，提供一些默认选项
      qualityOptions = [480, 720, 1080];
      qualityMapping = [
        { plyrValue: 480, hlsIndex: 0, label: '480p' },
        { plyrValue: 720, hlsIndex: 1, label: '720p' },
        { plyrValue: 1080, hlsIndex: 2, label: '1080p' }
      ];
      defaultQuality = 480;
    }

    console.log('创建Plyr配置 - 质量选项:', qualityOptions);
    console.log('创建Plyr配置 - 质量映射:', qualityMapping);
    console.log('创建Plyr配置 - 默认质量:', defaultQuality);

    return {
      controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'captions', 'settings', 'fullscreen'],
      settings: ['captions', 'quality', 'speed'],
      captions: { 
        active: true, 
        language: languageCode ? languageCode.split('-')[0] : 'zh', 
        update: true 
      },
      quality: {
        default: defaultQuality,
        options: qualityOptions,
        forced: true,
        onChange: (quality: number) => {
          console.log('Plyr分辨率切换到:', quality);
          const mapping = qualityMapping.find(m => m.plyrValue === quality);
          if (mapping) {
            console.log('映射到HLS索引:', mapping.hlsIndex);
            handleQualityChange(mapping.hlsIndex);
          }
        }
      },
      speed: { selected: 1, options: [0.5, 1, 1.5, 2] },
      iconUrl: plyrIconUrl,
      blankVideo: plyrBlankVideo,
      fullscreen: {
        enabled: true,
        fallback: true,
        iosNative: true,
      }
    };
  }, [languageCode, handleQualityChange]);

  // 初始化Plyr播放器
  const initPlyr = useCallback(async () => {
    if (!containerRef.current || isInitialized) return;
    asyncHls.then(async ({ default: Hls }) => {

      console.log('开始初始化播放器');
      destroyCurrentPlayer();

      const isHls = isHlsUrl(videoUrl);
      console.log('视频类型检测:', isHls ? 'HLS' : 'MP4', videoUrl);

      let levels: any[] = [];

      // 如果是HLS，先预加载获取分辨率信息
      if (isHls && Hls.isSupported()) {
        try {
          console.log('预加载HLS分辨率信息...');
          levels = await preloadHlsInfo(videoUrl);
          setQualityLevels(levels);
          console.log('预加载完成，获取到分辨率级别:', levels);
        } catch (error) {
          console.log('预加载HLS信息失败:', error);
          // 继续初始化，使用默认配置
        }
      }

      // 创建video元素
      const videoElement = document.createElement('video');
      videoElement.controls = true;
      videoElement.crossOrigin = 'anonymous';
      videoElement.preload = 'metadata';
      videoElement.playsInline = true;          // 关键：iOS 不强制全屏
      videoElement.setAttribute('webkit-playsinline', 'true');  // 兼容旧版
      
      videoElementRef.current = videoElement;
      
      if (!isHls) {
        // 普通视频文件
        const source = document.createElement('source');
        source.src = videoUrl;
        source.type = 'video/mp4';
        videoElement.appendChild(source);
      } else {
        // HLS视频，直接设置src
        videoElement.src = videoUrl;
      }

      if (subtitleUrl) {
        const srclang = languageCode ? languageCode.split('-')[0] : 'zh';
        
        const track = document.createElement('track');
        track.kind = 'subtitles';
        track.label = subtitleUrl.includes('blob:') ? '动态字幕' : 'English';
        track.srclang = srclang;
        track.src = subtitleUrl;
        track.default = true;
        videoElement.appendChild(track);
        setCurrentSubtitleUrl(subtitleUrl);
      }

      containerRef.current?.appendChild(videoElement);

      // 使用预加载的分辨率信息创建Plyr配置
      const plyrConfig = createPlyrConfig(levels);
      console.log('使用配置初始化Plyr:', plyrConfig);
      const player = new Plyr(videoElement, plyrConfig);

      // 如果是HLS视频，初始化hls.js
      if (isHls && Hls.isSupported()) {
        console.log('初始化HLS支持');
        const hls = new Hls({
          enableWorker: false, // 避免一些环境下的兼容性问题
          lowLatencyMode: true,
          backBufferLength: 90
        });
        
        hls.loadSource(videoUrl);
        hls.attachMedia(videoElement);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('HLS manifest已解析，分辨率选项已在初始化时设置');
        });
        
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS错误:', data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log('HLS网络错误，尝试重新加载');
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('HLS媒体错误，尝试恢复');
                hls.recoverMediaError();
                break;
              default:
                console.log('HLS无法恢复的错误，销毁HLS实例');
                hls.destroy();
                break;
            }
          }
        });

        // 监听分辨率级别变化
        hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
          console.log('HLS分辨率切换到:', data.level);
          setCurrentQuality(data.level);
          
          // 更新Plyr显示的当前质量，转换成分辨率值
          if (playerInstanceRef.current && playerInstanceRef.current.quality !== undefined) {
            const levels = hls.levels;
            if (levels && levels[data.level]) {
              playerInstanceRef.current.quality = levels[data.level].height;
              console.log('更新Plyr显示质量为:', levels[data.level].height);
            }
          }
        });
        
        hlsInstanceRef.current = hls;
      } else if (isHls && !Hls.isSupported() && videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari原生支持HLS
        console.log('使用Safari原生HLS支持');
        videoElement.src = videoUrl;
      } else if (isHls) {
        console.warn('当前浏览器不支持HLS播放');
      }

      if (onPlay) {
        let hasTriggered = false;
        player.on('play', () => {
          if (!hasTriggered) {
            hasTriggered = true;
            onPlay();
          }
        });
      }

      player.on('ready', () => {
        console.log('播放器就绪，开始设置字幕');
        // if (subtitleUrl) {
        //   const plyrPlayer = player as any;
        //   const videoElement = plyrPlayer.media;
        //   if (videoElement && videoElement.textTracks) {
        //     console.log('找到字幕轨道，数量:', videoElement.textTracks.length);
            
        //     for (let i = 0; i < videoElement.textTracks.length; i++) {
        //       const track = videoElement.textTracks[i];
        //       if (track.kind === 'subtitles' || track.kind === 'captions') {
        //         track.mode = 'showing';
        //         console.log(`启用字幕轨道 ${i}: ${track.label || track.language}`);
        //       }
        //     }
        //   }
          
        //   try {
        //     if (plyrPlayer.captions) {
        //       plyrPlayer.captions.active = true;
        //       console.log('通过Plyr配置启用字幕');
        //     }
        //   } catch (error) {
        //     console.log('Plyr字幕配置失败:', error);
        //   }
        // }
      });

      // 监听退出全屏事件
      player.on('exitfullscreen', () => {
        // 重新计算一次父级尺寸，防止塌陷
        window.dispatchEvent(new Event('resize')); // Plyr 内部会监听 resize 重新布局
        // console.log('退出全屏，强制重置所有定位');
        
        // setTimeout(() => {
        //   // 重置containerRef
        //   if (containerRef.current) {
        //     containerRef.current.style.cssText = `
        //       width: 100% !important;
        //       height: auto !important;
        //     `;
        //   }
        // }, 50);
      });

      // player.on('loadedmetadata', () => {
      //   console.log('视频元数据加载完成');
      //   const plyrPlayer = player as any;
      //   const videoElement = plyrPlayer.media;
      //   if (videoElement && videoElement.textTracks) {
      //     console.log('元数据加载后字幕轨道数量:', videoElement.textTracks.length);
      //     for (let i = 0; i < videoElement.textTracks.length; i++) {
      //       const track = videoElement.textTracks[i];
      //       if (track.kind === 'subtitles' || track.kind === 'captions') {
      //         track.mode = 'showing';
      //         console.log(`元数据加载后启用字幕轨道 ${i}`);
      //       }
      //     }
      //   }
      // });

      playerInstanceRef.current = player;
      setIsInitialized(true);
      
    });
  }, [videoUrl, subtitleUrl, languageCode, onPlay, isInitialized, destroyCurrentPlayer, isHlsUrl, handleQualityChange, preloadHlsInfo, createPlyrConfig]);

  const playerKey = useMemo(() => {
    return `${videoUrl}`;
  }, [videoUrl]);

  useEffect(() => {
    if (subtitleUrl && subtitleUrl !== currentSubtitleUrl && isInitialized) {
      console.log('字幕URL变化，更新字幕轨道');
      updateSubtitleTrack(subtitleUrl);
      setCurrentSubtitleUrl(subtitleUrl);
    } else if (subtitleUrl && !isInitialized) {
      console.log('播放器未初始化，初始化播放器');
      setTimeout(() => {
        initPlyr();
      }, 100);
    }
  }, [subtitleUrl, currentSubtitleUrl, isInitialized, updateSubtitleTrack, initPlyr]);

  useEffect(() => {
    if (videoUrl && !isInitialized) {
      console.log('视频URL变化，初始化播放器');
      setTimeout(() => {
        initPlyr();
      }, 100);
    }
  }, [videoUrl, isInitialized, initPlyr]);

  useEffect(() => {
    return () => {
      destroyCurrentPlayer();
    };
  }, [destroyCurrentPlayer]);

  return (
    <div className={`video-player ${className}`} key={playerKey}>
      <div className="player-container">
        <div 
          ref={containerRef}
          className="player-wrapper player-plyr"
        />
      </div>
    </div>
  );
});

VideoPlayer.displayName = "VideoPlayer";

export default VideoPlayer;